/* eslint-disable global-require */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _, { isArray, isFunction, isNil } from 'lodash';
import useBoolean from '../hooks/useBoolean.js';

const styles = {
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loading: {
    display: 'flex',
    position: 'absolute',
  },
  videoStyle: {
    display: 'flex',
    position: 'absolute',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
};

export const VideoContainer = forwardRef(
  (
    {
      url,
      poster,
      isMuted = false,
      isLooping = false,
      isAutoPlay = false,
      isPlaying = false,
      resizeMode = 'cover',
      playOnHover = false,
      testID,
      onLoadedMetadata,
      onVolumeChange,
      onProgress,
      onEnd,
      style,
      videoStyle,
    } = {},
    ref
  ) => {
    const videoRef = useRef();
    const { value: isLoaded, setTrue: setIsLoadedTrue, setFalse: setIsLoadedFalse } = useBoolean(false);
    const [isMouseOver, setMouseOver] = useState(false);
    // The web variant of VideoContainer requires a string for the video src attribute.
    // If a native-style object with a `uri` has been provided, convert it to a string
    const source = url?.uri || url;

    const containerStyles = useMemo(
      () => _.merge({}, ...[styles.container].concat(!isNil(style) ? (isArray(style) ? style : [style]) : [])),
      [style]
    );

    const videoStyles = useMemo(
      () => ({
        ...styles.videoStyle,
        objectFit: resizeMode,
        ...videoStyle,
      }),
      [resizeMode, isLoaded, videoStyle]
    );

    const renderLoadingIndicator = useCallback(() => {
      if (isLoaded || !poster) {
        return null;
      }
      return <img src={poster} style={styles.videoStyle} />;
    }, [isLoaded, poster]);

    const handleOnLoadedMetadata = useCallback(async (event) => {
      const lastVolume = parseFloat((await localStorage.getItem('volume')) ?? '1.0');

      const videoElement = event.target;
      if (videoElement && onLoadedMetadata && isFunction(onLoadedMetadata)) {
        // update video element volume based on last volume from local storage
        videoElement.volume = lastVolume;

        const metadata = {
          duration: videoElement.duration,
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
          volume: lastVolume,
        };

        onLoadedMetadata(metadata);
      }
    }, []);

    const handleOnEnded = useCallback(
      (event) => {
        const videoElement = event.target;
        if (videoElement && onEnd && isFunction(onEnd)) {
          onEnd();
        }
      },
      [onEnd]
    );

    const handleOnMouseEnter = useCallback(() => {
      setMouseOver(true);
    }, []);

    const handleOnMouseLeave = useCallback(() => {
      setMouseOver(false);
    }, []);

    useImperativeHandle(ref, () => ({
      play: () => {
        if (videoRef && videoRef.current) {
          videoRef.current.play();
        }
      },
      pause: () => {
        if (videoRef && videoRef.current) {
          videoRef.current.pause();
        }
      },
      requestFullscreen: () => {
        if (videoRef && videoRef.current) {
          videoRef.current.requestFullscreen();
        }
      },
      setPosition: (position) => {
        if (videoRef && videoRef.current) {
          const wasPlaying = videoRef.current.playing;

          if (wasPlaying) {
            videoRef.current.pause();
          }

          videoRef.current.currentTime = position / 1000;

          if (wasPlaying) {
            videoRef.current.play();
          }
        }
      },
      setVolume: (volume) => {
        const convertedVolume = volume / 100;

        if (videoRef && videoRef.current) {
          videoRef.current.volume = convertedVolume;
        }

        // save volume
        localStorage.setItem('volume', convertedVolume.toString());
      },
    }));

    // Reset isLoaded to false if source changes
    useEffect(() => {
      setIsLoadedFalse();
    }, [source]);

    useEffect(() => {
      if (!videoRef.current) {
        return;
      }
      if (isPlaying) {
        videoRef.current.play();
      } else if (!isAutoPlay) {
        videoRef.current.pause();
      }
    }, [isPlaying, isAutoPlay, videoRef.current]);

    useEffect(() => {
      if (!videoRef || !videoRef.current) {
        return;
      }

      if (playOnHover) {
        if (isMouseOver) {
          videoRef.current.play();
          return;
        }

        videoRef.current.pause();
      }
    }, [videoRef, isMouseOver, playOnHover]);

    useEffect(() => {
      const handleVideoProgress = (event) => {
        const videoElement = event.target;
        if (videoElement && onProgress && isFunction(onProgress)) {
          onProgress(videoElement.currentTime * 1000);
        }
      };

      const handleLoadedData = () => {
        setIsLoadedTrue();
      };

      if (!videoRef.current) {
        return;
      }

      videoRef.current.addEventListener('timeupdate', handleVideoProgress);
      videoRef.current.addEventListener('loadeddata', handleLoadedData);

      return () => {
        if (videoRef?.current) {
          videoRef.current.removeEventListener('timeupdate', handleVideoProgress);
          videoRef.current.removeEventListener('loadeddata', handleLoadedData);
        }
      };
    }, [onProgress]);

    return (
      <div key={source} style={containerStyles}>
        {renderLoadingIndicator()}
        <video
          ref={videoRef}
          muted={isMuted}
          loop={isLooping}
          autoPlay={isAutoPlay}
          playsInline
          disableRemotePlayback={true}
          preload="auto"
          style={videoStyles}
          src={source}
          poster={poster}
          type="video/mp4"
          data-testid={process.env.NODE_ENV === 'test' && (testID || 'video')}
          onLoadedMetadata={handleOnLoadedMetadata}
          onEnded={handleOnEnded}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      </div>
    );
  }
);
